import { useCallback, useEffect, useRef, useState } from 'react';

import { apagarPartesDaNota, enviarPartes, guardarParte, limparPartesDasNotas, partesGuardadas } from '@/comuns/filaDeAudio';
import { semRede } from '@/comuns/filaOffline';
import { PARTE_MS, notaEmAndamento, segundosGravados } from '@/data/notaDeIa';
import type { ModoDaNota, NotaDeIa, OpcoesDaNovaNota } from '@/data/notaDeIa';
import { dados } from '@/data/porta';
import { GravadorEmPartes, microfoneSuportado } from './gravadorEmPartes';

/**
 * A gravação da nota da IA no editor de atendimento.
 *
 * `gravando` e `pausado` são do microfone DESTE aparelho. A nota pode estar
 * `gravando` no servidor sem microfone nenhum ligado — a aba recarregou —, e
 * aí a tela oferece continuar ou concluir com o que já foi gravado.
 */
export interface NotaDaIa {
    notas: NotaDeIa[];
    /** A nota desta pessoa que ainda não foi decidida. */
    aberta: NotaDeIa | undefined;
    suportado: boolean;
    gravando: boolean;
    pausado: boolean;
    /** Enviando as partes e pedindo a nota à IA. */
    concluindo: boolean;
    segundos: number;
    /** Partes ainda neste aparelho, esperando transcrição. */
    pendentes: number;
    erro: string | null;
    comecar: (modo: ModoDaNota, opcoes: OpcoesDaNovaNota) => Promise<void>;
    continuar: () => Promise<void>;
    pausar: () => void;
    retomar: () => void;
    concluir: () => Promise<void>;
    descartar: () => Promise<void>;
    recarregar: () => Promise<void>;
}

interface OpcoesDoHook {
    atendimentoId: string;
    notasIniciais: NotaDeIa[];
    /** A pausa do cronômetro da consulta pausa a gravação junto. */
    consultaPausada: boolean;
    consultaAberta: boolean;
}

const INTERVALO_DE_CONSULTA_MS = 4000;

export function useNotaDaIa({ atendimentoId, notasIniciais, consultaPausada, consultaAberta }: OpcoesDoHook): NotaDaIa {
    const [notas, setNotas] = useState(notasIniciais);
    const [gravando, setGravando] = useState(false);
    const [pausado, setPausado] = useState(false);
    const [concluindo, setConcluindo] = useState(false);
    const [segundos, setSegundos] = useState(0);
    const [pendentes, setPendentes] = useState(0);
    const [erro, setErro] = useState<string | null>(null);

    const gravador = useRef<GravadorEmPartes | null>(null);
    const envio = useRef<Promise<unknown>>(Promise.resolve());
    const trava = useRef<WakeLockSentinel | null>(null);
    const montado = useRef(true);

    const aberta = notaEmAndamento(notas);
    const abertaId = aberta?.id;

    useEffect(() => {
        montado.current = true;
        return () => {
            montado.current = false;
        };
    }, []);

    useEffect(() => {
        setNotas(notasIniciais);
    }, [notasIniciais]);

    const recarregar = useCallback(async () => {
        const lidas = await dados().notasDeIaDaConsulta(atendimentoId);
        if (montado.current) setNotas(lidas);
    }, [atendimentoId]);

    const contarPendentes = useCallback(async (notaId: string) => {
        const n = (await partesGuardadas(notaId)).length;
        if (montado.current) setPendentes(n);
        return n;
    }, []);

    /* Um envio de cada vez: a parte que fecha durante um envio espera a vez. */
    const enviar = useCallback((notaId: string) => {
        const proximo = envio.current.then(async () => {
            if (semRede()) return { restantes: await contarPendentes(notaId) };
            const r = await enviarPartes(notaId, (p) => dados().enviarParteDaNotaDeIa(notaId, p));
            await contarPendentes(notaId);
            if (montado.current) setErro(r.falha ?? null);
            return r;
        });
        envio.current = proximo.catch(() => undefined);
        return proximo;
    }, [contarPendentes]);

    /* O que sobrou de nota que já não grava sai do aparelho. */
    const decididas = notas.filter((n) => n.situacao !== 'gravando').map((n) => n.id).join(',');
    useEffect(() => {
        if (decididas) void limparPartesDasNotas(decididas.split(','));
    }, [decididas]);

    /* A nota aberta mudou (ou a aba recarregou): o tempo já gravado volta ao relógio. */
    useEffect(() => {
        if (!abertaId || gravador.current) return;
        void (async () => {
            const [servidor, locais] = await Promise.all([dados().partesDaNotaDeIa(abertaId), partesGuardadas(abertaId)]);
            if (!montado.current || gravador.current) return;
            setSegundos(Math.round(segundosGravados(servidor) + segundosGravados(locais)));
            setPendentes(locais.length);
        })();
    }, [abertaId]);

    /* A rede voltou: as partes guardadas saem. */
    useEffect(() => {
        if (!abertaId) return;
        const aoVoltar = () => void enviar(abertaId);
        window.addEventListener('online', aoVoltar);
        return () => window.removeEventListener('online', aoVoltar);
    }, [abertaId, enviar]);

    /* Relógio da gravação: só conta enquanto o microfone grava. */
    useEffect(() => {
        if (!gravando || pausado) return;
        const t = window.setInterval(() => setSegundos((s) => s + 1), 1000);
        return () => window.clearInterval(t);
    }, [gravando, pausado]);

    /* Nota sendo escrita pela IA em outra aba (ou antes de recarregar): acompanha. */
    useEffect(() => {
        if (concluindo || !notas.some((n) => n.situacao === 'gerando')) return;
        const t = window.setInterval(() => void recarregar(), INTERVALO_DE_CONSULTA_MS);
        return () => window.clearInterval(t);
    }, [notas, concluindo, recarregar]);

    /* Fechar a aba no meio da gravação perde a parte que está sendo gravada. */
    useEffect(() => {
        if (!gravando) return;
        const avisar = (ev: BeforeUnloadEvent) => {
            ev.preventDefault();
        };
        window.addEventListener('beforeunload', avisar);
        return () => window.removeEventListener('beforeunload', avisar);
    }, [gravando]);

    const segurarTela = useCallback(async () => {
        try {
            trava.current = (await navigator.wakeLock?.request('screen')) ?? null;
        } catch {
            /* sem Wake Lock, a tela pode apagar; o microfone segue enquanto o navegador deixar */
        }
    }, []);

    const soltarTela = useCallback(() => {
        void trava.current?.release().catch(() => undefined);
        trava.current = null;
    }, []);

    const ligar = useCallback(async (nota: NotaDeIa, ordemInicial: number) => {
        const g = new GravadorEmPartes({
            parteMs: PARTE_MS,
            aoFecharParte: async ({ ordem, segundos: s, audio }) => {
                await guardarParte({ notaId: nota.id, ordem, segundos: s, audio, guardadaEm: new Date().toISOString() });
                await contarPendentes(nota.id);
                void enviar(nota.id);
            },
            aoPerderMicrofone: () => {
                gravador.current = null;
                soltarTela();
                if (!montado.current) return;
                setGravando(false);
                setPausado(false);
                setErro('O microfone parou. O que já foi gravado está guardado: continue ou conclua a nota.');
            },
        });
        await g.iniciar(ordemInicial);
        gravador.current = g;
        setGravando(true);
        setPausado(false);
        setErro(null);
        void segurarTela();
    }, [contarPendentes, enviar, segurarTela, soltarTela]);

    const comecar = useCallback(async (modo: ModoDaNota, opcoes: OpcoesDaNovaNota) => {
        if (!microfoneSuportado()) throw new Error('Este navegador não grava áudio.');
        const nota = await dados().iniciarNotaDeIa(atendimentoId, modo, opcoes);
        setNotas((atuais) => [nota, ...atuais]);
        setSegundos(0);
        setPendentes(0);
        try {
            await ligar(nota, 0);
        } catch (e) {
            /* Sem microfone, a nota recém-aberta não tem por que ficar. */
            await dados().descartarNotaDeIa(nota.id).catch(() => undefined);
            await recarregar();
            throw e;
        }
    }, [atendimentoId, ligar, recarregar]);

    const continuar = useCallback(async () => {
        if (!aberta || aberta.situacao !== 'gravando' || gravador.current) return;
        const [servidor, locais] = await Promise.all([dados().partesDaNotaDeIa(aberta.id), partesGuardadas(aberta.id)]);
        const ultima = Math.max(-1, ...servidor.map((p) => p.ordem), ...locais.map((p) => p.ordem));
        await ligar(aberta, ultima + 1);
    }, [aberta, ligar]);

    const pausar = useCallback(() => {
        gravador.current?.pausar();
        if (gravador.current) setPausado(true);
    }, []);

    const retomar = useCallback(() => {
        gravador.current?.retomar();
        if (gravador.current) setPausado(false);
    }, []);

    const concluir = useCallback(async () => {
        if (!aberta) return;
        const notaId = aberta.id;
        setConcluindo(true);
        setErro(null);
        try {
            const g = gravador.current;
            gravador.current = null;
            if (g) await g.concluir();
            soltarTela();
            setGravando(false);
            setPausado(false);

            if (aberta.situacao === 'gravando') {
                const r = await enviar(notaId);
                if (r.restantes > 0) {
                    setErro(semRede()
                        ? 'Sem conexão: as partes ficaram guardadas neste aparelho. Conclua de novo quando a rede voltar.'
                        : `Faltam ${r.restantes} ${r.restantes === 1 ? 'parte' : 'partes'} para enviar. Tente concluir de novo.`);
                    return;
                }
            }
            setNotas((atuais) => atuais.map((n) => (n.id === notaId ? { ...n, situacao: 'gerando' } : n)));
            const gerada = await dados().gerarNotaDeIa(notaId);
            if (montado.current) setNotas((atuais) => atuais.map((n) => (n.id === notaId ? gerada : n)));
        } catch (e) {
            if (montado.current) setErro(e instanceof Error ? e.message : 'Não foi possível concluir a nota.');
            await recarregar().catch(() => undefined);
        } finally {
            if (montado.current) setConcluindo(false);
        }
    }, [aberta, enviar, recarregar, soltarTela]);

    const descartar = useCallback(async () => {
        if (!aberta) return;
        gravador.current?.cancelar();
        gravador.current = null;
        soltarTela();
        setGravando(false);
        setPausado(false);
        await dados().descartarNotaDeIa(aberta.id);
        await apagarPartesDaNota(aberta.id);
        setSegundos(0);
        setPendentes(0);
        setErro(null);
        await recarregar();
    }, [aberta, recarregar, soltarTela]);

    /* O cronômetro da consulta manda: pausou a consulta, pausa a gravação. */
    useEffect(() => {
        if (!gravador.current) return;
        if (consultaPausada) {
            gravador.current.pausar();
            setPausado(true);
        } else {
            gravador.current.retomar();
            setPausado(false);
        }
    }, [consultaPausada]);

    /* Consulta encerrada com o microfone ligado: a gravação conclui sozinha. */
    useEffect(() => {
        if (!consultaAberta && gravador.current) void concluir();
    }, [consultaAberta, concluir]);

    /* Saiu da tela: a parte em curso fecha e fica guardada, e o microfone é solto. */
    useEffect(() => () => {
        void gravador.current?.concluir();
        gravador.current = null;
        void trava.current?.release().catch(() => undefined);
    }, []);

    return {
        notas, aberta, suportado: microfoneSuportado(), gravando, pausado, concluindo, segundos, pendentes, erro,
        comecar, continuar, pausar, retomar, concluir, descartar, recarregar,
    };
}
